import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

export default dayjs;
export type { Dayjs };

/**
 * Gets the appropriate date format string based on language/locale.
 * Each locale has its preferred date format.
 *
 * @param lang - Language code (e.g., 'en-us', 'ru-ru', 'en', 'ru')
 * @returns Format string for dayjs (e.g., 'DD.MM.YYYY' for Russian, 'MM/DD/YYYY' for US English)
 */
export function getDateFormat(lang: string | undefined): string {
  if (!lang) {
    return "DD.MM.YYYY"; // Default fallback
  }

  const normalizedLang = lang.toLowerCase();

  // Map language codes to their preferred date formats
  const formatMap: Record<string, string> = {
    // Russian locales
    "ru-ru": "DD.MM.YYYY",
    "ru": "DD.MM.YYYY",

    // US English - MM/DD/YYYY is standard
    "en-us": "MM/DD/YYYY",

    // UK/Australian English - DD/MM/YYYY
    "en-gb": "DD/MM/YYYY",
    "en-au": "DD/MM/YYYY",

    // Generic fallbacks
    "en": "MM/DD/YYYY", // Default English is US format
  };

  return formatMap[normalizedLang] || "DD.MM.YYYY";
}

/**
 * Converts a date-like value to a valid dayjs object or null.
 * Used for form field initialization to ensure DatePicker receives valid input.
 *
 * @param value - A string, Date object, dayjs object, or undefined
 * @returns Valid dayjs object or null if value is invalid/undefined
 */
export function toFormDate(value: string | Date | Dayjs | undefined): Dayjs | null {
  if (!value) {
    return null;
  }

  const dayjsDate = dayjs(value);

  if (!dayjsDate.isValid()) {
    return null;
  }

  return dayjsDate;
}

/**
 * Converts a dayjs object back to ISO 8601 string format.
 * Used when serializing form data for API submission.
 *
 * @param value - A dayjs object or undefined
 * @returns ISO 8601 formatted string or undefined if value is null/undefined
 */
export function fromFormDate(value: Dayjs | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  if (!dayjs.isDayjs(value)) {
    return undefined;
  }

  if (!value.isValid()) {
    return undefined;
  }

  return value.toISOString();
}
